import { useState } from "react";
import { DateTime } from "luxon";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import type { RevenueTrendData } from "@/hooks/useRevenueTrendData";
import type { Granularity } from "@/lib/periods";
import type { FilterState } from "@/types/filters";
import { getCSSVar } from "@/lib/chart";
import { niceYBounds } from "@/lib/chartScale";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

const TOGGLE_ITEM_CLS =
  "h-auto text-[12px] px-3 py-1 border-black/15 bg-white text-gray-600 font-normal hover:bg-gray-50 hover:text-gray-600 transition-colors data-[state=on]:bg-[var(--brand-dark-teal)] data-[state=on]:text-white data-[state=on]:border-[var(--brand-dark-teal)] data-[state=on]:z-10";

const GRAN_LABELS = { day: "Daily", week: "Weekly", month: "Monthly" } as const;

type RevMetric = "seenHr" | "schedHr" | "appt";

const METRIC_META: Record<RevMetric, { label: string; toggleLabel: string }> = {
  seenHr: { label: "Med. Net Rev. per Pet Seen Hour", toggleLabel: "$/seen hr" },
  schedHr: { label: "Med. Net Rev. per Scheduled Hour", toggleLabel: "$/sched hr" },
  appt: { label: "Med. Net Rev. per Completed Appt.", toggleLabel: "$/appt" },
};

function metricValue(point: RevenueTrendData["points"][number], metric: RevMetric): number {
  switch (metric) {
    case "seenHr":
      return point.medRevPerSeenHour;
    case "schedHr":
      return point.medRevPerSchedHour;
    case "appt":
      return point.medRevPerAppt;
  }
}

interface RevenueMetricChartProps {
  trend: RevenueTrendData;
  granularity: FilterState["revGranularity"];
  // Groupings the selected period actually spans — a chart with one point
  // is not a trend, so the rest are disabled rather than hidden.
  availableGranularities: Granularity[];
  // True when the period is calendar-based and grouped by week, so the chart
  // is showing fewer days than the KPI cards above it.
  partialWeeksShown: boolean;
  setGranularity: (g: FilterState["revGranularity"]) => void;
}

export function RevenueMetricChart({
  trend,
  granularity,
  availableGranularities,
  partialWeeksShown,
  setGranularity,
}: RevenueMetricChartProps) {
  const [lineColor] = useState(() => getCSSVar("--brand-dark-teal") || "#044f59");
  const [metric, setMetric] = useState<RevMetric>("seenHr");

  // Two-part day/week labels split onto two axis lines; month is a single part.
  const labels =
    granularity === "month"
      ? trend.points.map((p) => p.label)
      : trend.points.map((p) => p.label.split(" "));
  const values = trend.points.map((p) => Math.round(metricValue(p, metric)));

  const data: ChartData<"line"> = {
    labels,
    datasets: [
      {
        label: METRIC_META[metric].label,
        data: values,
        borderColor: lineColor,
        backgroundColor: `${lineColor}15`,
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: lineColor,
        borderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            const point = trend.points[items[0]?.dataIndex ?? -1];
            if (!point) return items[0]?.label ?? "";
            return granularity === "day"
              ? DateTime.fromISO(point.bucketKey).toFormat("ccc M/d")
              : point.label;
          },
          label: (ctx) => `${ctx.dataset.label}: $${(ctx.parsed.y ?? 0).toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        ...niceYBounds(values),
        ticks: { font: { size: 11 }, color: "#6b6b68", callback: (v) => `$${v}` },
        grid: { color: "#e5e4e0" },
      },
      x: {
        ticks: {
          font: { size: 11 },
          color: "#6b6b68",
          maxRotation: 0,
          minRotation: 0,
        },
        grid: { display: false },
      },
    },
  };

  return (
    <Card className="px-5 py-4 gap-0 shadow-none border-black/10">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[16px] font-medium">Revenue Metric Trend</div>
        <ToggleGroup
          type="single"
          variant="outline"
          value={metric}
          onValueChange={(v) => {
            if (v) setMetric(v as RevMetric);
          }}
        >
          {(Object.keys(METRIC_META) as RevMetric[]).map((m) => (
            <ToggleGroupItem key={m} value={m} className={TOGGLE_ITEM_CLS}>
              {METRIC_META[m].toggleLabel}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="text-[12px] text-gray-500 mb-3">{GRAN_LABELS[granularity]}</div>

      <div className="flex-1 min-h-0 relative" style={{ height: 260 }}>
        <Line data={data} options={options} />
      </div>

      <div className="flex items-center gap-2 mt-3 justify-end">
        <span className="text-[11px] text-gray-500 font-medium">Group by</span>
        <ToggleGroup
          type="single"
          variant="outline"
          value={granularity}
          onValueChange={(v) => {
            if (v) setGranularity(v as FilterState["revGranularity"]);
          }}
        >
          {(["day", "week", "month"] as const).map((g) => (
            <ToggleGroupItem
              key={g}
              value={g}
              disabled={!availableGranularities.includes(g)}
              className={TOGGLE_ITEM_CLS}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      {partialWeeksShown && (
        <p className="text-[11px] text-gray-400 mt-2">
          The first and last points are part-weeks — a calendar period does not start or end on a
          Monday. Their totals are smaller for that reason; the per-DVM rates divide by the days
          actually elapsed, so those stay comparable.
        </p>
      )}
    </Card>
  );
}
